import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Icon, Popover, Tooltip, useTheme } from "@mui/material"; // Assuming MUI Icon is used
import AppContext from "../context/context";
import { useDarkMode } from "../context/darkModeContext";
import { getIcon } from "./sidebarData";
import { isMobile } from "../../globalStyles/mobile";
import * as RiIcons from "react-icons/ri";

const SidebarLink = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  list-style: none;
  text-decoration: none;
  color: var(--text-primary);
  font-size: 14px;
  margin-top: 7px;
  border-radius: 8px;
  &:hover {
    cursor: pointer;
    color: var(--text-primary);
  }
`;
// background: ${(props) => "green"};
const DropdownLink = styled(Link)`
  display: flex;
  align-items: center;
  font-size: 12px;
  text-decoration: none;
  color: var(--text-primary);
  border-radius: 8px;
  margin-left: 30px;
  padding: 7px;
  max-height: 38px;
  &:hover {
    margin-left: 30px;
    background: var(--hover-bg);
    cursor: pointer;
    color: var(--text-primary);
    border-radius: 8px;
    .MuiSvgIcon-root {
      color: var(--text-primary) !important;
    }
  }
`;
const SubMenuWrapper = styled.div`
  position: relative;
  margin-left: 5px;

  display: ${(props) => (props.hidebar ? "none" : "block")};

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 15px;
    width: 2px;
    height: ${(props) => (props.isLastItem ? "calc(50% - 9px)" : "100%")};
    background-color: var(--submenu-line);
    z-index: 0;
  }
`;

const CurvedLine = styled.div`
  position: absolute;
  top: 10px;
  left: 15px;
  width: 15px;
  height: 10px;
  border-left: 2px solid var(--submenu-line);
  border-bottom: 2px solid var(--submenu-line);
  border-radius: 0 0 0 10px;
  z-index: 1;

  /* Conditional rendering for curved line */
  display: ${(props) =>
    props.hidebar ? "none" : "block"}; /* Hide if hidebar is true */
`;

const SubMenu = ({
  item,
  onClickSubMenu,
  onClickMenu,
  hideSideBar,
  activeSubMenuTitle,
  setActiveSubMenuTitle,
}) => {
  const mainTitleRef = useRef(null);
  const subTitleRefsMap = useRef({});
  const [isMainTitleTruncated, setIsMainTitleTruncated] = useState(false);
  const [subTitlesTruncated, setSubTitlesTruncated] = useState({});

  // Helper function to check user roles from localStorage
  const checkUserRoles = (requiredRoles) => {
    try {
      const storedRoles = localStorage.getItem("roles");

      if (!storedRoles) return false;

      const userRoles = JSON.parse(storedRoles);
      if (!Array.isArray(userRoles)) return false;

      // Check if user has admin role
      const hasAdmin = userRoles.some((role) => {
        if (!role) return false;
        // Handle both string and object formats
        const roleName = typeof role === "string" ? role : role.name;
        return roleName && roleName.toLowerCase().includes("admin");
      });

      // Check if user has client role (Standard or Premium)
      const hasClient = userRoles.some((role) => {
        if (!role) return false;
        // Handle both string and object formats
        const roleName = typeof role === "string" ? role : role.name;
        return (
          roleName &&
          (roleName.toLowerCase().includes("client - standard") ||
            roleName.toLowerCase().includes("client - premium") ||
            roleName.toLowerCase().includes("client"))
        );
      });

      // If required roles include admin and user has admin, allow access
      if (requiredRoles.includes("admin") && hasAdmin) {
        return true;
      }

      // If required roles include client and user has client, allow access
      if (requiredRoles.includes("client") && hasClient) {
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error checking user roles:", error);
      return false;
    }
  };

  const { colors } = useDarkMode();
  const {
    currentMenu,
    setCurrentMenu,
    currentSubMenu,
    setCurrentSubMenu,
    user,
    setUser,
    setIsSidebarOpen,
    isSidebarOpen,
  } = useContext(AppContext);
  const location = useLocation();
  // const [subnav, setSubnav] = useState(false)
  const currentPath = location.pathname;
  const hideSubNavInSidebar = Boolean(item.path && item.subNav);
  const sidebarSubNav = hideSubNavInSidebar ? null : item.subNav;
  const subnav = Boolean(sidebarSubNav) && activeSubMenuTitle === item.pkey;
  const childPathActive = item.subNav?.some(
    (subItem) => subItem.path === currentPath,
  );
  const isSelected = (menuItem) =>
    (Boolean(menuItem.path) && currentPath === menuItem.path) ||
    (hideSubNavInSidebar && childPathActive) ||
    (!item.subNav && currentMenu.pkey === menuItem.pkey);
  const isItemDisabled = (menuItem) => menuItem?.accessible === false;
  const isDisabled = isItemDisabled(item);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const defaultColor = "#ff6a6e";
  const [mainTextColor, setMainTextColor] = useState(
    theme.palette.text.main || defaultColor,
  );
  const [hoverColor, setHoverColor] = useState(
    theme.palette.primary.main || defaultColor,
  );
  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  useEffect(() => {
    setMainTextColor(localStorage.getItem("text_color")?.text_color || "#FFF");
    setHoverColor(
      localStorage.getItem("primary_color")?.primary_color || defaultColor,
    );
  }, [currentPath]);
  const isOpen = Boolean(anchorEl);

  // useEffect(() => {
  //   if (currentMenu.pkey === item.pkey) {
  //     setSubnav(true)
  //   }
  // }, [currentMenu, item.pkey])
  useEffect(() => {
    if (!sidebarSubNav) return;
    if (currentMenu.pkey === item.pkey) {
      setActiveSubMenuTitle(item.pkey);
    }
  }, [currentMenu, item.pkey]);
  useEffect(() => {
    if (currentSubMenu.pkey) {
      setCurrentMenu({ title: currentSubMenu.parent });
      localStorage.setItem("parent_tab", currentSubMenu.parent);
    }
  }, [currentSubMenu]);
  // useEffect(() => {
  //   if (item.pkey === localStorage.getItem('parent_tab')) {
  //     setSubnav(true)
  //   }
  // }, [])
  useEffect(() => {
    if (!sidebarSubNav) return;
    if (item.pkey === localStorage.getItem("parent_tab")) {
      setActiveSubMenuTitle(item.pkey);
    }
  }, []);

  // Check if main title is truncated
  useEffect(() => {
    const checkTruncation = () => {
      if (mainTitleRef.current) {
        const isOverflowing =
          mainTitleRef.current.scrollWidth > mainTitleRef.current.clientWidth;
        setIsMainTitleTruncated(isOverflowing);
      }
    };

    // Use setTimeout to check after render completes
    const timer = setTimeout(checkTruncation, 0);
    window.addEventListener("resize", checkTruncation);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", checkTruncation);
    };
  }, [item.title, hideSideBar]);

  // Memoized function to update subtitle truncation state
  const updateSubTitleTruncation = useCallback(() => {
    const newTruncatedState = {};
    Object.entries(subTitleRefsMap.current).forEach(([pkey, ref]) => {
      if (ref) {
        newTruncatedState[pkey] = ref.scrollWidth > ref.clientWidth;
      }
    });
    setSubTitlesTruncated(newTruncatedState);
  }, []);

  // Check subtitles truncation after render
  useEffect(() => {
    const timer = setTimeout(updateSubTitleTruncation, 0);
    window.addEventListener("resize", updateSubTitleTruncation);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateSubTitleTruncation);
    };
  }, [updateSubTitleTruncation, subnav]);

  // Helper to set subtitle refs
  const setSubTitleRef = useCallback((ref, pkey) => {
    if (ref) {
      subTitleRefsMap.current[pkey] = ref;
    }
  }, []);
  return (
    <div style={{ marginInline: 10, paddingBottom: 5 }}>
      <motion.div
        onClick={() => {
          if (sidebarSubNav && item.path && !isDisabled) {
            localStorage.setItem("path", item.path);
            onClickMenu();
            setCurrentSubMenu("");
            setCurrentMenu(item);
            localStorage.setItem("parent_tab", item.pkey);
            setActiveSubMenuTitle(item.pkey);
          } else if (sidebarSubNav) {
            setActiveSubMenuTitle(subnav ? null : item.pkey);
          } else if (!isDisabled) {
            localStorage.setItem("path", item?.path ? item.path : "/");
            onClickMenu();
            setCurrentSubMenu("");
            setCurrentMenu(item);
            localStorage.setItem("parent_tab", item.pkey);
            setActiveSubMenuTitle(null);
          }
        }}
      >
        <Tooltip
          title={
            hideSideBar
              ? item.pkey
              : isDisabled
                ? "You don't have access to this item"
                : ""
          }
          placement="right"
        >
          <SidebarLink
            hoverColor={hoverColor}
            onClick={(e) => {
              if (isDisabled && !sidebarSubNav) {
                e.preventDefault();
                return;
              }
              if (item.url) {
                window.open(item.url, "_blank");
              }
              if (!isSidebarOpen && sidebarSubNav) {
                handlePopoverOpen(e);
              }
            }}
            isSelected={isSelected(item)}
            subNav={item.subNav}
            style={{
              paddingLeft: 5,
              display: "flex",
              alignItems: "center",
              opacity: isDisabled ? 0.45 : 1,
              cursor: isDisabled && !sidebarSubNav ? "not-allowed" : "pointer",
              background:
                isSelected(item) && item.subNav ? colors.optionBg : "",
            }}
            to={!isDisabled && item.path && item.path !== "" ? item.path : null}
          >
            {/* Sidebar Icon */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 24, // Ensures proper spacing
              }}
            >
              <Icon
                style={{
                  marginTop: 7,
                  height: 30,
                  width: 25,
                  color: isSelected(item) ? "#fff" : "",
                }}
              >
                {getIcon(
                  item.icon,
                  currentMenu.pkey === item.pkey
                    ? hoverColor
                    : colors.textPrimary,
                  undefined,
                  undefined,
                  item.title,
                )}
              </Icon>
            </div>

            {!hideSideBar && (
              <Tooltip
                title={isMainTitleTruncated ? item.title : ""}
                placement="top"
              >
                <span
                  ref={mainTitleRef}
                  style={{
                    fontFamily: "Poppins",
                    fontSize: 13,
                    fontWeight: 500,
                    color:
                      isSelected(item) || currentMenu.pkey === item.pkey
                        ? hoverColor
                        : "",
                    flex: 1,
                    textAlign: "left",
                    paddingLeft: 10,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    marginLeft: 5,
                    marginTop: 2,
                    maxWidth: 150,
                  }}
                >
                  {item.title}
                </span>
              </Tooltip>
            )}
            {sidebarSubNav && subnav ? (
              <RiIcons.RiArrowUpSFill />
            ) : sidebarSubNav ? (
              <RiIcons.RiArrowDownSFill />
            ) : null}
          </SidebarLink>
        </Tooltip>
      </motion.div>
      {subnav ? (
        sidebarSubNav?.map((subItem, index) => {
          const isSelectedSub =
            currentSubMenu?.pkey === subItem.pkey &&
            currentSubMenu?.parent === item.pkey;
          const isSubDisabled = isItemDisabled(subItem);
          return subItem.parent === item.pkey ? (
            <SubMenuWrapper
              isLastItem={index + 1 === sidebarSubNav?.length}
              key={index}
              hidebar={hideSideBar}
            >
              <CurvedLine hidebar={hideSideBar} />
              <motion.div key={index}>
                <Tooltip
                  title={
                    hideSideBar
                      ? subItem.pkey
                      : isSubDisabled
                        ? "You don't have access to this item"
                        : ""
                  }
                >
                  <DropdownLink
                    style={{
                      color: isSelectedSub
                        ? colors.textPrimary
                        : colors.textSecondary,
                      justifyContent: hideSideBar ? "center" : "start",
                      maxWidth: hideSideBar ? "50px" : "100%",
                      paddingLeft: hideSideBar ? 0 : "1rem",
                      marginTop: index === 0 ? 5 : 0,
                      opacity: isSubDisabled ? 0.45 : 1,
                      cursor: isSubDisabled ? "not-allowed" : "pointer",
                    }}
                    onClick={(e) => {
                      if (isSubDisabled) {
                        e.preventDefault();
                        return;
                      }
                      if (subItem.url) {
                        window.open(subItem.url, "_blank");
                      } else {
                        localStorage.setItem(
                          "path",
                          subItem?.path ? subItem?.path : "/",
                        );
                        setCurrentSubMenu(subItem);
                        onClickSubMenu && onClickSubMenu();
                        setCurrentMenu("");
                      }
                    }}
                    to={
                      !isSubDisabled && subItem.path && subItem.path !== ""
                        ? subItem.path
                        : null
                    }
                  >
                    {hideSideBar ? (
                      <Icon
                        style={{
                          marginBottom: 10,
                          height: 35,
                          color: isSelectedSub ? "#fff" : "",
                        }}
                      >
                        {getIcon(
                          subItem.icon,
                          isSelectedSub && !currentMenu?.pkey
                            ? "#FFF"
                            : colors.textPrimary,
                          undefined,
                          undefined,
                          subItem.title,
                        )}
                      </Icon>
                    ) : null}

                    <div
                      style={{
                        color: isSelectedSub
                          ? colors.textPrimary
                          : colors.textSecondary,
                      }}
                    >
                      <Tooltip
                        title={
                          subTitlesTruncated[subItem.pkey] ? subItem.title : ""
                        }
                        placement="bottom"
                      >
                        <p
                          ref={(ref) => setSubTitleRef(ref, subItem.pkey)}
                          style={{
                            marginLeft: 10,
                            fontFamily: "Poppins",
                            fontSize: 12,
                            fontWeight: 500,
                            textAlign: "left",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 120,
                          }}
                        >
                          {hideSideBar ? "" : subItem.title}
                        </p>
                      </Tooltip>
                    </div>
                  </DropdownLink>
                </Tooltip>
              </motion.div>
            </SubMenuWrapper>
          ) : null;
        })
      ) : (
        <></>
      )}
      {!isMobile() ? (
        <Popover
          open={isOpen}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <div style={{ padding: "10px", width: "100%", maxHeight: 300 }}>
            {sidebarSubNav?.map((subItem, index) => {
              const isSelectedSub =
                currentSubMenu?.pkey === subItem.pkey &&
                currentSubMenu?.parent === item.pkey;
              const isSubDisabled = isItemDisabled(subItem);
              return checkUserRoles(subItem?.roles || []) &&
                subItem.parent === item.pkey ? (
                <motion.div key={index}>
                  <DropdownLink
                    style={{
                      color: isSelectedSub
                        ? colors.textPrimary
                        : colors.textSecondary,
                      background: isSelectedSub ? colors.optionBg : "",
                      justifyContent: "start",
                      width: "100%",
                      marginLeft: 0,
                      opacity: isSubDisabled ? 0.45 : 1,
                      cursor: isSubDisabled ? "not-allowed" : "pointer",
                    }}
                    onClick={(e) => {
                      if (isSubDisabled) {
                        e.preventDefault();
                        return;
                      }
                      if (subItem.url) {
                        window.open(subItem.url, "_blank");
                      } else {
                        localStorage.setItem(
                          "path",
                          subItem?.path ? subItem?.path : "/",
                        );
                        onClickSubMenu && onClickSubMenu();
                        setCurrentSubMenu(subItem);
                        setCurrentMenu("");
                        handlePopoverClose();
                      }
                    }}
                    to={
                      !isSubDisabled && subItem.path && subItem.path !== ""
                        ? subItem.path
                        : null
                    }
                  >
                    {subItem.title}
                  </DropdownLink>
                </motion.div>
              ) : null;
            })}
          </div>
        </Popover>
      ) : (
        <></>
      )}
    </div>
  );
};

export default SubMenu;
